import { useMemo } from "react";
import { Card, Descriptions, Typography, Space, Row, Col } from "antd";
import { MoneyCollectOutlined } from "@ant-design/icons";
import type { CommissionComponentModel, DocInfo, FileItem } from "@services/api";
import DownloadButton from "./DownloadButton";

const { Text, Title } = Typography;

const formatCurrencyRUB = (value: number | null | undefined): string => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

interface Props {
  commission?: CommissionComponentModel;
  docs?: DocInfo[];
  className?: string;
}

export default function BankOffer({ commission, docs, className }: Props) {
  const bankCommission = commission?.attributes?.bankCommission;

  // Ищем bg_bill и берём единственный файл
  const billFile: FileItem | null = useMemo(() => {
    const doc = docs?.find((d) => d.docType === "bg_bill");
    const file = doc?.files?.[0];
    return file && file.fileId?.trim() ? file : null;
  }, [docs]);

  // Если нет суммы комиссии и файла - не рендерим
  if (!bankCommission && !billFile) {
    return null;
  }

  const valueTextStyle: React.CSSProperties = { fontVariantNumeric: "tabular-nums" };

  return (
    <Card className={className} bordered bodyStyle={{ padding: 20 }}>
      {/* Заголовок с иконкой */}
      <Space align="center" size={8} style={{ marginBottom: 8 }}>
        <MoneyCollectOutlined style={{ color: "#1677ff", fontSize: 20 }} />
        <Title level={5} style={{ margin: 0 }}>Предложение банка</Title>
      </Space>

      {/* Слева — Сумма, справа — кнопка */}
      <Row justify="space-between" align="top" gutter={16} style={{ marginBottom: 8 }}>
        <Col flex="auto">
          <Row gutter={8} align="middle" wrap={false}>
            <Col>
              <Typography.Text type="secondary">Сумма комиссии:</Typography.Text>
            </Col>
            <Col flex="auto">
              <Typography.Title level={5} style={{ margin: 0 }}>
                {formatCurrencyRUB(bankCommission)}
              </Typography.Title>
            </Col>
          </Row>
        </Col>
        <Col flex="none">
          {billFile && (
            <DownloadButton
              type="primary" ghost
              file={billFile}
              label="Скачать счёт на оплату"
              style={{ marginLeft: 12 }}
            />
          )}
        </Col>
      </Row>
    </Card>
  );
}
